import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Mic,
  MicOff,
  Square,
  Play,
  Pause,
  Clock,
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/shared/Button';
import { ExamContentGuard } from '../components/exam/ExamContentGuard';
import { DevToolsBlockOverlay } from '../components/exam/DevToolsBlockOverlay';
import { useDevToolsGuard } from '../hooks/useDevToolsGuard';
import { examService, type ResumeAttemptResponse } from '../services/exam.service';

interface SpeakingTask {
  id: string;
  title: string;
  prompt: string;
  context: string;
  prepTime: number;
  speakTime: number;
}

type TaskPhase = 'prep' | 'speaking' | 'review' | 'done';

function mapResumeToTasks(data: ResumeAttemptResponse): SpeakingTask[] {
  const exam = data.exam;
  if (!exam?.questions?.length) return [];
  return exam.questions.map((q) => ({
    id: q._id,
    title: q.passageTitle || (q.prompt ? q.prompt.slice(0, 40) + '…' : q.text?.slice(0, 40) || 'Task'),
    prompt: q.prompt || q.text || '',
    context: q.context || '',
    prepTime: q.prepTime ?? 20,
    speakTime: q.speakTime ?? 60,
  }));
}

function getInitialRecordings(data: ResumeAttemptResponse): Record<string, string> {
  const out: Record<string, string> = {};
  const saved = data.savedAnswers || [];
  saved.forEach((a) => {
    if (a.answer && typeof a.answer === 'string' && a.answer.trim().length > 0) {
      out[String(a.questionId)] = a.answer;
    }
  });
  return out;
}

export const SpeakingExamPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const attemptId = searchParams.get('attemptId');
  const { devToolsAllowed, devToolsOpen } = useDevToolsGuard(searchParams);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [tasks, setTasks] = useState<SpeakingTask[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<TaskPhase>('prep');
  const [prepTimeLeft, setPrepTimeLeft] = useState(0);
  const [speakTimeLeft, setSpeakTimeLeft] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrls, setAudioUrls] = useState<Record<string, string>>({});
  const [localBlobUrls, setLocalBlobUrls] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [remainingTotalSeconds, setRemainingTotalSeconds] = useState(0);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [waveformData, setWaveformData] = useState<number[]>(Array(50).fill(0));
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [isPlayingBack, setIsPlayingBack] = useState(false);

  const prepTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const speakTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const waveformIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioPlaybackRef = useRef<HTMLAudioElement | null>(null);
  const localBlobsRef = useRef<Record<string, Blob>>({});
  const uploadedAudioRef = useRef<Record<string, string>>({});
  const recordingSettledRef = useRef<Promise<void> | null>(null);
  const resolveRecordingSettledRef = useRef<(() => void) | null>(null);
  const pendingUploadsRef = useRef<Set<Promise<void>>>(new Set());
  const reRecordingRef = useRef<Set<string>>(new Set());
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!attemptId) {
      navigate('/dashboard');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    examService
      .resumeAttempt(attemptId)
      .then((data) => {
        if (cancelled) return;
        if (data.exam?.type !== 'speaking') {
          setLoadError('This is not a speaking exam.');
          setLoading(false);
          return;
        }
        const mapped = mapResumeToTasks(data);
        if (mapped.length === 0) {
          setLoadError('No tasks in this exam.');
          setLoading(false);
          return;
        }
        setTasks(mapped);
        const initialRecordings = getInitialRecordings(data);
        uploadedAudioRef.current = initialRecordings;
        setAudioUrls(initialRecordings);
        setExpiresAt(data.expiresAt);
        const remaining = Math.max(
          0,
          Math.floor((new Date(data.expiresAt).getTime() - Date.now()) / 1000),
        );
        setRemainingTotalSeconds(remaining);
        setLoading(false);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setLoadError(err?.response?.data?.message || 'Could not load exam.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (prepTimerRef.current) clearInterval(prepTimerRef.current);
      if (speakTimerRef.current) clearInterval(speakTimerRef.current);
      if (waveformIntervalRef.current) clearInterval(waveformIntervalRef.current);
    };
  }, [attemptId, navigate]);

  const current = tasks[currentIndex];
  const recordedCount = new Set([
    ...Object.keys(localBlobUrls),
    ...Object.keys(audioUrls).filter((id) => audioUrls[id]?.length > 0),
  ]).size;

  const handleSubmit = useCallback(async () => {
    if (!attemptId || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      // A currently active recording has no blob until MediaRecorder fires stop.
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop();
        streamRef.current?.getTracks().forEach((track) => track.stop());
        if (speakTimerRef.current) clearInterval(speakTimerRef.current);
        if (waveformIntervalRef.current) clearInterval(waveformIntervalRef.current);
        setIsRecording(false);
        mediaRecorderRef.current = null;
      }
      if (recordingSettledRef.current) await recordingSettledRef.current;
      await Promise.all(Array.from(pendingUploadsRef.current));
      if (Array.from(reRecordingRef.current).some((id) => !localBlobsRef.current[id])) {
        throw new Error(t('exam.speaking.recordingUploadFailed'));
      }
      // Retry uploading any recordings that failed earlier
      for (const questionId of Object.keys(localBlobsRef.current)) {
        if (!uploadedAudioRef.current[questionId]) {
          try {
            const blob = localBlobsRef.current[questionId];
            const { url } = await examService.uploadAttemptAudio(attemptId, questionId, blob);
            await examService.saveAnswer(attemptId, questionId, url);
            uploadedAudioRef.current[questionId] = url;
            reRecordingRef.current.delete(questionId);
            setAudioUrls((prev) => ({ ...prev, [questionId]: url }));
          } catch {
            throw new Error(t('exam.speaking.recordingUploadFailed'));
          }
        }
      }
      await examService.submitExam(attemptId);
      navigate(`/results?attemptId=${attemptId}`);
    } catch (err: any) {
      submittingRef.current = false;
      setSubmitting(false);
      setInlineError(err?.response?.data?.message || err?.message || t('exam.common.failedToSubmit'));
      setShowSubmitModal(true);
    }
  }, [attemptId, navigate, t]);

  useEffect(() => {
    if (!expiresAt || !tasks.length || !attemptId) return;
    const expiresAtMs = new Date(expiresAt).getTime();
    const timer = setInterval(() => {
      const remaining = Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000));
      setRemainingTotalSeconds(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
        void handleSubmit();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [attemptId, expiresAt, tasks.length, handleSubmit]);

  useEffect(() => {
    const selectedTask = tasks[currentIndex];
    if (!selectedTask) return;
    setPrepTimeLeft(selectedTask.prepTime);
    setSpeakTimeLeft(selectedTask.speakTime);
    setPhase('prep');
    setIsRecording(false);
    setPlaybackUrl(null);
    setIsPlayingBack(false);
  }, [currentIndex, tasks]);

  const formatTime = (totalSeconds: number) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const stopRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (speakTimerRef.current) clearInterval(speakTimerRef.current);
    if (waveformIntervalRef.current) clearInterval(waveformIntervalRef.current);
    setIsRecording(false);
    mediaRecorderRef.current = null;
  }, []);

  const startRecording = useCallback(async () => {
    if (!current || !attemptId || submittingRef.current || (expiresAt && Date.now() >= new Date(expiresAt).getTime())) return;
    setInlineError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Submission may have started while the microphone permission prompt was open.
      if (submittingRef.current || (expiresAt && Date.now() >= new Date(expiresAt).getTime())) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      const preferredMimeType = ['audio/webm;codecs=opus', 'audio/mp4']
        .find((type) => MediaRecorder.isTypeSupported(type));
      const mr = preferredMimeType
        ? new MediaRecorder(stream, { mimeType: preferredMimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      recordingSettledRef.current = new Promise<void>((resolve) => {
        resolveRecordingSettledRef.current = resolve;
      });
      chunksRef.current = [];
      const questionId = current.id;
      mr.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        const blob = new Blob(chunksRef.current, {
          type: mr.mimeType || chunksRef.current[0]?.type || 'audio/webm',
        });
        if (blob.size === 0) {
          setPhase('review');
          resolveRecordingSettledRef.current?.();
          resolveRecordingSettledRef.current = null;
          return;
        }
        // Store locally first for instant playback
        const blobUrl = URL.createObjectURL(blob);
        localBlobsRef.current[questionId] = blob;
        setLocalBlobUrls((prev) => ({ ...prev, [questionId]: blobUrl }));
        setPhase('review');
        // Upload in background
        setUploading(true);
        const pending = (async () => {
          try {
            const { url } = await examService.uploadAttemptAudio(attemptId, questionId, blob);
            await examService.saveAnswer(attemptId, questionId, url);
            uploadedAudioRef.current[questionId] = url;
            reRecordingRef.current.delete(questionId);
            setAudioUrls((prev) => ({ ...prev, [questionId]: url }));
          } catch (err: any) {
            setInlineError(err?.response?.data?.message || 'Upload failed — will retry on submit.');
          } finally {
            setUploading(false);
          }
        })();
        pendingUploadsRef.current.add(pending);
        await pending;
        pendingUploadsRef.current.delete(pending);
        resolveRecordingSettledRef.current?.();
        resolveRecordingSettledRef.current = null;
      };
      mr.start(200);
      setIsRecording(true);
      setSpeakTimeLeft(current.speakTime);
      waveformIntervalRef.current = setInterval(() => {
        setWaveformData((prev) => [...prev.slice(1), Math.random() * 80 + 20]);
      }, 100);
      speakTimerRef.current = setInterval(() => {
        setSpeakTimeLeft((prev) => {
          if (prev <= 1) {
            if (speakTimerRef.current) clearInterval(speakTimerRef.current);
            stopRecording();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      resolveRecordingSettledRef.current?.();
      resolveRecordingSettledRef.current = null;
      setInlineError('Microphone access is needed to record.');
      setPhase('prep');
    }
  }, [current, attemptId, expiresAt, stopRecording]);

  const handleStopRecording = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  const reRecord = () => {
    if (uploading || pendingUploadsRef.current.size > 0 || submittingRef.current) return;
    setPhase('prep');
    setPrepTimeLeft(current?.prepTime ?? 20);
    setSpeakTimeLeft(current?.speakTime ?? 60);
    setInlineError(null);
    if (current) {
      reRecordingRef.current.add(current.id);
      // Revoke old local blob URL
      if (localBlobUrls[current.id]) {
        URL.revokeObjectURL(localBlobUrls[current.id]);
      }
      delete localBlobsRef.current[current.id];
      setLocalBlobUrls((prev) => { const next = { ...prev }; delete next[current.id]; return next; });
      setAudioUrls((prev) => { const next = { ...prev }; delete next[current.id]; return next; });
      delete uploadedAudioRef.current[current.id];
    }
    setWaveformData(Array(50).fill(0));
  };

  const skipPrep = useCallback(() => {
    if (prepTimerRef.current) clearInterval(prepTimerRef.current);
    setPrepTimeLeft(0);
    setPhase('speaking');
  }, []);

  const togglePlayback = useCallback(() => {
    const localUrl = current ? localBlobUrls[current.id] : null;
    if (!localUrl) return;
    if (playbackUrl && isPlayingBack) {
      audioPlaybackRef.current?.pause();
      setPlaybackUrl(null);
      setIsPlayingBack(false);
      return;
    }
    setPlaybackUrl(localUrl);
    setIsPlayingBack(true);
    const audio = new Audio(localUrl);
    audioPlaybackRef.current = audio;
    audio.onended = () => {
      setPlaybackUrl(null);
      setIsPlayingBack(false);
    };
    audio.play().catch(() => {});
  }, [current, localBlobUrls, playbackUrl, isPlayingBack]);

  // When phase transitions to 'speaking', auto-start recording
  useEffect(() => {
    if (phase === 'speaking' && !isRecording && !uploading) {
      const timeout = setTimeout(() => startRecording(), 50);
      return () => clearTimeout(timeout);
    }
  }, [phase, isRecording, uploading, startRecording]);

  useEffect(() => {
    if (phase === 'prep') {
      prepTimerRef.current = setInterval(() => {
        setPrepTimeLeft((prev) => {
          if (prev <= 1) {
            if (prepTimerRef.current) clearInterval(prepTimerRef.current);
            setPhase('speaking');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (prepTimerRef.current) clearInterval(prepTimerRef.current);
    };
  }, [phase]);

  const navigateTask = (offset: number) => {
    const next = currentIndex + offset;
    if (next < 0 || next >= tasks.length) return;
    setCurrentIndex(next);
  };

  const isLowTime = remainingTotalSeconds < 180;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
          <p className="text-gray-600">Loading exam…</p>
        </div>
      </div>
    );
  }

  if (loadError || tasks.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md text-center">
          <p className="text-gray-800 font-medium mb-4">{loadError || 'No tasks available.'}</p>
          <Button variant="primary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const hasRecording = (id: string) => !!(localBlobUrls[id] || audioUrls[id]?.length > 0);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-rose-50 to-white">
      <DevToolsBlockOverlay show={devToolsOpen} />
      <div className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
                <Mic className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{t('exam.speaking.title')}</h1>
                <p className="text-xs text-gray-500">
                  {tasks.length} {t('exam.speaking.speakingTasks')}
                </p>
              </div>
            </div>
            <div
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-mono text-lg ${
                isLowTime ? 'bg-error/10 text-error animate-pulse' : 'bg-gray-900 text-white'
              }`}
            >
              <Clock className="w-5 h-5" />
              <span className="font-bold">{formatTime(remainingTotalSeconds)}</span>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 bg-gray-200 h-2 rounded-full">
              <div
                className="h-2 rounded-full bg-rose-500 transition-all duration-300"
                style={{ width: `${(recordedCount / tasks.length) * 100}%` }}
              />
            </div>
            <span className="text-sm text-gray-600 whitespace-nowrap">
              {recordedCount}/{tasks.length} {t('exam.common.recorded')}
            </span>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-5xl mx-auto px-4 py-6 w-full">
        {/* AI Disclosure (EU AI Act Art. 50) */}
        <div className="mb-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900">
          <span className="shrink-0 font-bold mt-0.5">🤖</span>
          <span>
            <strong>AI-beoordeling:</strong> Jouw gesproken antwoorden worden automatisch getranscribeerd en beoordeeld via AI (OpenAI Whisper + GPT). De score is indicatief en vervangt geen officiële examinering.
          </span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 bg-rose-100 text-rose-700 font-bold rounded-full text-sm">
                  {t('exam.common.task')} {currentIndex + 1}
                </span>
                <h2 className="text-lg font-bold text-gray-900">{current.title}</h2>
              </div>
              <ExamContentGuard disabled={devToolsAllowed}>
                {current.context && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                    <p className="text-sm font-medium text-amber-800">{t('exam.common.situation')}</p>
                    <p className="text-sm text-amber-700">{current.context}</p>
                  </div>
                )}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-900 font-medium">{current.prompt}</p>
                </div>
              </ExamContentGuard>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${phase === 'prep' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-400'}`}>
                  <span className="w-6 h-6 rounded-full bg-current/20 flex items-center justify-center text-xs font-bold">1</span>
                  {t('exam.speaking.preparation')}
                </div>
                <div className="w-8 h-0.5 bg-gray-200" />
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${phase === 'speaking' ? 'bg-rose-100 text-rose-700' : phase === 'review' || phase === 'done' ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-400'}`}>
                  <span className="w-6 h-6 rounded-full bg-current/20 flex items-center justify-center text-xs font-bold">2</span>
                  {t('exam.speaking.record')}
                </div>
                <div className="w-8 h-0.5 bg-gray-200" />
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${phase === 'review' || phase === 'done' ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-400'}`}>
                  <span className="w-6 h-6 rounded-full bg-current/20 flex items-center justify-center text-xs font-bold">3</span>
                  {t('exam.speaking.review')}
                </div>
              </div>

              {phase === 'prep' && (
                <div className="text-center">
                  <div className="w-24 h-24 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-4">
                    <Clock className="w-10 h-10 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t('exam.speaking.prepTime')}</h3>
                  <p className="text-gray-600 mb-4">{t('exam.speaking.prepDescription')}</p>
                  <div className="text-5xl font-mono font-bold text-amber-600 mb-6">{formatTime(prepTimeLeft)}</div>
                  <Button variant="secondary" onClick={skipPrep}>{t('exam.speaking.beginRecording')} →</Button>
                </div>
              )}

              {phase === 'speaking' && (
                <div className="text-center">
                  {!isRecording ? (
                    <div className="flex flex-col items-center gap-4">
                      <Loader2 className="w-10 h-10 animate-spin text-rose-500" />
                      <p className="text-gray-500">{t('exam.speaking.startingMic') || 'Starting microphone…'}</p>
                    </div>
                  ) : (
                    <>
                      <div className="w-24 h-24 mx-auto bg-rose-600 rounded-full flex items-center justify-center mb-4 animate-pulse shadow-lg shadow-rose-200">
                        <Mic className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-rose-600 mb-2">{t('exam.speaking.recording')}</h3>
                      <div className="text-5xl font-mono font-bold text-rose-600 mb-4">{formatTime(speakTimeLeft)}</div>
                      <div className="h-20 bg-gray-50 rounded-xl flex items-end justify-center gap-0.5 px-4 mb-6">
                        {waveformData.map((height, i) => (
                          <div key={i} className="w-1 bg-rose-500 rounded-t transition-all duration-100" style={{ height: `${Math.max(10, height)}%` }} />
                        ))}
                      </div>
                      <Button variant="primary" className="bg-gray-800 hover:bg-gray-900" onClick={handleStopRecording} icon={<Square className="w-5 h-5" />} disabled={uploading}>
                        {uploading ? t('exam.common.uploading') || 'Uploading…' : t('exam.speaking.stopRecording')}
                      </Button>
                    </>
                  )}
                </div>
              )}

              {(phase === 'review' || phase === 'done') && (
                <div className="text-center">
                  <div className="w-24 h-24 mx-auto bg-success/20 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-10 h-10 text-success" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t('exam.speaking.recordingComplete')}</h3>
                  <p className="text-gray-600 mb-4">{t('exam.speaking.recordingCompleteDesc')}</p>
                  {inlineError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                      <p className="text-sm text-red-700">{inlineError}</p>
                    </div>
                  )}
                  <div className="flex justify-center gap-3">
                    <Button variant="secondary" onClick={togglePlayback} icon={isPlayingBack ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} disabled={!hasRecording(current.id)}>
                      {isPlayingBack ? t('exam.speaking.pause') : t('exam.speaking.play')}
                    </Button>
                    <Button variant="tertiary" onClick={reRecord} disabled={uploading || submitting} icon={<RotateCcw className="w-4 h-4" />}>
                      {t('exam.speaking.reRecord')}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <Button variant="tertiary" onClick={() => navigateTask(-1)} disabled={currentIndex === 0} icon={<ChevronLeft className="w-4 h-4" />}>
                {t('exam.common.previousTask')}
              </Button>
              <div className="flex gap-3">
                {currentIndex < tasks.length - 1 ? (
                  <Button variant="primary" onClick={() => navigateTask(1)} className="flex-row-reverse" icon={<ChevronRight className="w-4 h-4" />}>
                    {t('exam.common.nextTask')}
                  </Button>
                ) : (
                  <Button variant="primary" className="bg-success hover:bg-success/90" onClick={() => setShowSubmitModal(true)} icon={<CheckCircle className="w-4 h-4" />}>
                    {t('exam.common.submitExam')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-4 sticky top-32">
              <h3 className="font-bold text-gray-900 mb-3">{t('exam.common.tasks')}</h3>
              <div className="space-y-2 mb-4">
                {tasks.map((task, idx) => (
                  <button
                    key={task.id}
                    onClick={() => setCurrentIndex(idx)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all ${
                      currentIndex === idx ? 'bg-rose-100 border-2 border-rose-500' : hasRecording(task.id) ? 'bg-success/10 border border-success/30' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${currentIndex === idx ? 'text-rose-700' : 'text-gray-700'}`}>
                        {idx + 1}. {task.title}
                      </span>
                      {hasRecording(task.id) ? <CheckCircle className="w-4 h-4 text-success" /> : <MicOff className="w-4 h-4 text-gray-400" />}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{t('exam.common.speakTime')}: {task.speakTime}s</div>
                  </button>
                ))}
              </div>
              <div className="bg-rose-50 rounded-xl p-3 mb-4">
                <p className="text-xs font-medium text-rose-800 mb-2">🎙️ {t('exam.common.tips')}</p>
                <ul className="text-xs text-rose-700 space-y-1">
                  <li>• {t('exam.speaking.tips.speakClearly')}</li>
                  <li>• {t('exam.speaking.tips.readSituation')}</li>
                  <li>• {t('exam.speaking.tips.usePrepTime')}</li>
                  <li>• {t('exam.speaking.tips.canRerecord')}</li>
                </ul>
              </div>
              <Button variant="primary" className="w-full bg-success hover:bg-success/90" onClick={() => setShowSubmitModal(true)}>
                {t('exam.common.submitExam')}
              </Button>
            </div>
          </div>
        </div>
      </main>

      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-warning" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{t('exam.common.confirmSubmit')}</h3>
                <p className="text-sm text-gray-500">{t('exam.common.cannotUndo')}</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              {tasks.map((task, idx) => (
                <div key={task.id} className="flex justify-between items-center py-1">
                  <span className="text-sm text-gray-600">{t('exam.common.task')} {idx + 1}:</span>
                  <span className={`text-sm font-medium ${hasRecording(task.id) ? 'text-success' : 'text-error'}`}>
                    {hasRecording(task.id) ? `✓ ${t('exam.common.isRecorded')}` : `✗ ${t('exam.common.notRecorded')}`}
                  </span>
                </div>
              ))}
            </div>
            {recordedCount < tasks.length && (
              <p className="text-sm text-error mb-4">
                ⚠️ {t('exam.common.unrecordedWarning', { count: tasks.length - recordedCount })}
              </p>
            )}
            {inlineError && <p role="alert" className="text-sm text-error mb-4">{inlineError}</p>}
            <div className="flex gap-3">
              <Button variant="tertiary" className="flex-1" onClick={() => setShowSubmitModal(false)} disabled={submitting}>
                {t('exam.common.backToExam')}
              </Button>
              <Button variant="primary" className="flex-1 bg-success hover:bg-success/90" loading={submitting} onClick={handleSubmit} icon={<CheckCircle className="w-4 h-4" />}>
                {t('exam.common.submit')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpeakingExamPage;
